import { ChatIcon, CheckIcon, ChevronDownIcon, RepeatClockIcon, TimeIcon, WarningTwoIcon } from '@chakra-ui/icons'
import {
  Button,
  Flex,
  FormLabel,
  Heading,
  IconButton,
  Input,
  InputAddon,
  InputGroup,
  Link,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Select,
  Spacer,
  Stack,
  Text,
  Tooltip,
  useDisclosure
} from '@chakra-ui/react'
import React, { useEffect, useState } from 'react'

import Paginator from '../../../components/paginator'
import { RequestService } from '../../../services/Request/RequestService'
import { statuses } from './RequestConsts'
import { RequestDeleteDialog } from './RequestDeleteDialog'
import { RequestViewModal } from './RequestViewModal'

import './Request.css'

export default function RequestsPage() {
  const requestService = new RequestService()
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [requests, setRequests] = useState<any[]>([])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [modalRequest, setModalRequest] = useState<any>({ roles: [] })
  const [requestToDelete, setRequestToDelete] = useState<any>(null)
  const { isOpen: deleteDialogIsOpen, onOpen: deleteDialogOnOpen, onClose: deleteDialogIsClose } = useDisclosure()
  const cancelRef = React.useRef()
  const [order, setOrder] = useState<{ column: number; dir: string }>({ column: 7, dir: 'desc' })
  const [requestTypes, setRequestTypes] = useState<string[]>([])
  const [requestConsultants, setRequestConsultants] = useState<string[]>([])
  const [openedRequests, setOpenedRequests] = useState<{
    open: number
    in_progress: number
    waiting_payment: number
    waiting_for_a_place: number
    in_management_analysis: number
  }>({ open: 0, in_progress: 0, waiting_payment: 0, waiting_for_a_place: 0, in_management_analysis: 0 })
  const [filters, setFilters] = useState({
    length: 25,
    tipo_soliciacao: '',
    consultor_de_sucesso: '',
    formato_curso: '',
    status: 'active',
    created_at: {
      from: '',
      to: ''
    },
    solved_at: {
      from: '',
      to: ''
    },
    search: {
      value: ''
    }
  })

  const handleCloseModal = () => {
    setIsModalOpen(false)
    loadRequests(page)
  }

  const loadRequests = async (page: number) => {
    const { recordsFiltered, data } = await requestService.list({
      start: (page - 1) * filters.length,
      order: [order],
      ...filters
    })

    setRequests(data)

    setTotalPages(Math.ceil(recordsFiltered / filters.length))

    calcOpenedRequests()
  }

  useEffect(() => {
    loadRequests(page)
  }, [order, filters])

  useEffect(() => {
    const promises = [requestService.getRequestsTypes(), requestService.getRequestsConsultants()]

    Promise.all(promises).then(([types, consultants]) => {
      setRequestTypes(types.requestTypes)
      setRequestConsultants(consultants.consultants)
    })
  }, [])

  const calcOpenedRequests = async () => {
    const openRequests = await requestService.getOpenRequests(filters.consultor_de_sucesso, {
      from: filters.created_at.from,
      to: filters.created_at.to
    })
    setOpenedRequests(openRequests.openedRequests)
  }

  const handleOpenModal = async (requestId: number) => {
    const request = await requestService.get(requestId)

    setModalRequest(request)
    setIsModalOpen(true)
  }

  const handleDeleteDialog = (request: any) => {
    deleteDialogOnOpen()
    setRequestToDelete(request)
  }

  const refreshRequest = async (requestId: number) => {
    const request = await requestService.get(requestId)

    setModalRequest(request)
  }

  const handleOrder = (column: number) => {
    if (order.column == column) {
      setOrder({ column, dir: order.dir == 'asc' ? 'desc' : 'asc' })
    } else {
      setOrder({ column, dir: 'asc' })
    }
  }

  const handleStatusChange = async (requestId: number, status: string) => {
    await requestService.setNewStatus(requestId, status)
    loadRequests(page)
  }

  const handleSolvedTooltip = (request: any) => {
    return (
      <Stack>
        <Text>Concluído em: {new Date(request.solved_at.slice(0, -5)).toLocaleDateString('pt-BR')} {new Date(request.solved_at.slice(0, -5)).toLocaleTimeString('pt-BR').slice(0, 5)}</Text>
        <Text>Tempo de resolução: {Math.floor(request.tempo_resolucao / 60)} horas e {request.tempo_resolucao % 60} minutos</Text>
      </Stack>
    )
  }

  return (
    <>
      <Flex mb="2" justifyContent="space-between" direction="row">
        <Heading as="h1" size="lg" mb="10" textTransform="uppercase">
          Gerenciamento de solicitações de alunos
        </Heading>
        <div>
          <Text fontSize="md" color="gray.500" textAlign="right" mr="2">
            Solicitações em aberto: {openedRequests.open}
          </Text>
          <Text fontSize="md" color="gray.500" textAlign="right" mr="2">
            Solicitações em andamento: {openedRequests.in_progress}
          </Text>
          <Text fontSize="md" color="gray.500" textAlign="right" mr="2">
            Solicitações aguardando pagamento: {openedRequests.waiting_payment}
          </Text>
          <Text fontSize="md" color="gray.500" textAlign="right" mr="2">
            Solicitações aguardando vaga: {openedRequests.waiting_for_a_place}
          </Text>
          <Text fontSize="md" color="gray.500" textAlign="right" mr="2">
            Solicitações em análise da gestão: {openedRequests.in_management_analysis}
          </Text>
        </div>
      </Flex>

      <div className="row mb-4">
        <div className="col-12 col-md-11">
          <div className="row mb-2">
            <div className="col-12 col-sm-6 col-md-4 col-lg">
              <FormLabel htmlFor="request-type" fontSize="sm" fontWeight="normal">
                Tipo de solicitação
              </FormLabel>
              <Select
                size="sm"
                variant="outline"
                id="request-type"
                onChange={(e) => setFilters({ ...filters, tipo_soliciacao: e.target.value })}
                value={filters.tipo_soliciacao}
              >
                <option value="">Todos</option>
                {requestTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </Select>
            </div>
            <div className="col-12 col-sm-6 col-md-4 col-lg">
              <FormLabel htmlFor="status" fontSize="sm" fontWeight="normal">
                Status
              </FormLabel>
              <Select
                size="sm"
                variant="outline"
                id="status"
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                value={filters.status}
              >
                <option value="">Todos</option>
                <option value="active">Aberto/Em andamento</option>
                <option value="open">Aberto</option>
                <option value="in_progress">Em andamento</option>
                <option value="waiting_payment">Aguardando Pagamento</option>
                <option value="waiting_for_a_place">Aguardando Vaga</option>
                <option value="in_management_analysis">Em análise da gestão</option>
                <option value="solved">Resolvido</option>
              </Select>
            </div>
            <div className="col-12 col-sm-6 col-md-4 col-lg">
              <FormLabel htmlFor="date-start" fontSize="sm" fontWeight="normal">
                Data da solicitação
              </FormLabel>
              <InputGroup display="flex" alignItems="center" size="sm">
                <Input
                  type="date"
                  size="sm"
                  variant="outline"
                  id="date-start"
                  value={filters.created_at.from}
                  onChange={(e) => setFilters({ ...filters, created_at: { ...filters.created_at, from: e.target.value } })}
                  max={filters.created_at.to}
                />
                <InputAddon>Até</InputAddon>
                <Input
                  type="date"
                  size="sm"
                  variant="outline"
                  id="date-end"
                  value={filters.created_at.to}
                  onChange={(e) => setFilters({ ...filters, created_at: { ...filters.created_at, to: e.target.value } })}
                  min={filters.created_at.from}
                />
              </InputGroup>
            </div>
          </div>
          <div className="row">
            <div className="col-12 col-sm-6 col-md-4 col-lg">
              <FormLabel htmlFor="consultant" fontSize="sm" fontWeight="normal">
                Consultor/Setor
              </FormLabel>
              <Select
                size="sm"
                variant="outline"
                id="consultant"
                onChange={(e) => setFilters({ ...filters, consultor_de_sucesso: e.target.value })}
                value={filters.consultor_de_sucesso}
              >
                <option value="">Todos</option>
                <option value="-1">Não informado</option>
                {requestConsultants.map((consultant) => (
                  <option key={consultant} value={consultant}>
                    {consultant}
                  </option>
                ))}
              </Select>
            </div>
            <div className="col-12 col-sm-6 col-md-4 col-lg">
              <FormLabel htmlFor="formato-de-ensino" fontSize="sm" fontWeight="normal">
                Formato de ensino
              </FormLabel>
              <Select
                size="sm"
                variant="outline"
                id="formato-de-ensino"
                onChange={(e) => setFilters({ ...filters, formato_curso: e.target.value })}
                value={filters.formato_curso}
              >
                <option value="">Todos</option>
                <option value="Presencial">Presencial</option>
                <option value="EAD Ao Vivo">EAD Ao Vivo</option>
                <option value="Online (videoaulas)">Online</option>
              </Select>
            </div>
            <div className="col-12 col-sm-6 col-md-4 col-lg">
              <FormLabel htmlFor="date-start" fontSize="sm" fontWeight="normal">
                Data de resolução
              </FormLabel>
              <InputGroup display="flex" alignItems="center" size="sm">
                <Input
                  type="date"
                  size="sm"
                  variant="outline"
                  id="date-start"
                  value={filters.solved_at.from}
                  onChange={(e) => setFilters({ ...filters, solved_at: { ...filters.solved_at, from: e.target.value } })}
                  max={filters.solved_at.to}
                />
                <InputAddon>Até</InputAddon>
                <Input
                  type="date"
                  size="sm"
                  variant="outline"
                  id="date-end"
                  value={filters.solved_at.to}
                  onChange={(e) => setFilters({ ...filters, solved_at: { ...filters.solved_at, to: e.target.value } })}
                  min={filters.solved_at.from}
                />
              </InputGroup>
            </div></div>
        </div>
        <div className="col-12 col-md-1 d-flex align-items-center justify-content-center">
          <Tooltip label="Atualizar listagem" aria-label="Atualizar listagem" hasArrow>
            <IconButton
              aria-label="Atualizar"
              icon={<i className="lni lni-reload" />}
              colorScheme="magenta"
              size="sm"
              w="100%"
              py="5"
              mt="5"
              onClick={() => loadRequests(page)}
            />
          </Tooltip>
        </div>
      </div>
      <Flex mb="4">
        <Flex flex="1" alignItems="center">
          <FormLabel htmlFor="per-page" fontSize="sm" fontWeight="normal">
            Mostrar
          </FormLabel>
          <Select
            size="sm"
            variant="outline"
            id="per-page"
            w="75px"
            mr="2"
            onChange={(e) => setFilters({ ...filters, length: parseInt(e.target.value) })}
            value={filters.length}
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </Select>
          <FormLabel htmlFor="per-page" fontSize="sm" fontWeight="normal">
            resultados por página
          </FormLabel>
        </Flex>
        <Spacer />
        <Flex flex="1" alignContent="center" justifyContent="flex-end">
          <FormLabel htmlFor="search" fontSize="sm" fontWeight="normal">
            Pesquisar
          </FormLabel>
          <Input
            type="search"
            size="sm"
            variant="outline"
            id="search"
            w="200px"
            onChange={(e) => setFilters({ ...filters, search: { value: e.target.value } })}
            value={filters.search.value}
          />
        </Flex>
      </Flex>

      <div className="table-container">
        <table className="table table-striped" id="requests-table">
          <thead>
            <tr>
              <th
                className={`ordering ${order.column == 0 ? 'active' : ''} ${order.dir}`}
                onClick={() => handleOrder(0)}
              >
                #
              </th>
              <th
                className={`ordering ${order.column == 1 ? 'active' : ''} ${order.dir}`}
                onClick={() => handleOrder(1)}
              >
                Tipo de Solicitação
              </th>
              <th
                className={`ordering ${order.column == 2 ? 'active' : ''} ${order.dir}`}
                onClick={() => handleOrder(2)}
              >
                Consultor(a)/Setor
              </th>
              <th
                className={`ordering ${order.column == 3 ? 'active' : ''} ${order.dir}`}
                onClick={() => handleOrder(3)}
              >
                Aluno(a)
              </th>
              <th
                className={`ordering ${order.column == 4 ? 'active' : ''} ${order.dir}`}
                onClick={() => handleOrder(4)}
              >
                Responsável
              </th>
              <th
                className={`ordering ${order.column == 5 ? 'active' : ''} ${order.dir}`}
                onClick={() => handleOrder(5)}
              >
                Curso
              </th>
              <th
                className={`ordering ${order.column == 6 ? 'active' : ''} ${order.dir}`}
                onClick={() => handleOrder(6)}
              >
                Telefone
              </th>
              <th
                className={`ordering ${order.column == 7 ? 'active' : ''} ${order.dir}`}
                onClick={() => handleOrder(7)}
              >
                Data
              </th>
              <th
                className={`ordering ${order.column == 8 ? 'active' : ''} ${order.dir}`}
                onClick={() => handleOrder(8)}
              >
                Status
              </th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {requests.map((request) => (
              <tr key={request.id}>
                <td>{request.id}</td>
                <Tooltip
                  label={request.tipoDeSolicitacao}
                  aria-label={request.tipoDeSolicitacao}
                  hasArrow
                  placement="top"
                >
                  <td className="text-elipsis">{request.tipoDeSolicitacao}</td>
                </Tooltip>
                <td>{request.consultor_de_sucesso}</td>
                <td>{request.your_name}</td>
                <Tooltip
                  label={request.nome_responsavel}
                  aria-label={request.nome_responsavel}
                  hasArrow
                  placement="top"
                >
                  <td>
                    {request.nome_responsavel != '' && request.nome_responsavel}
                    {request.nome_responsavel == '' && (
                      <Text color="gray.400" align="center">
                        -
                      </Text>
                    )}
                  </td>
                </Tooltip>
                <td>{`Curso ${request.formato_curso.toLowerCase()} - ${request.course}`}</td>
                <td>
                  <Link
                    href={`https://wa.me/55${request.phone.replace(/\D/g, '')}`}
                    target="_blank"
                    rel="noreferrer"
                    color="blue.500"
                  >
                    {request.phone}
                  </Link>
                </td>
                <td>
                  {new Date(request.created_at.slice(0, -5)).toLocaleDateString('pt-BR')}{' '}
                  {new Date(request.created_at.slice(0, -5)).toLocaleTimeString('pt-BR').slice(0, 5)}
                </td>
                <td>
                  <Menu>
                    <MenuButton
                      as={Button}
                      rightIcon={<ChevronDownIcon />}
                      size="xs"
                      colorScheme={statuses[request.status].color}
                      color={statuses[request.status].textColor}
                    >
                      {statuses[request.status].text}
                    </MenuButton>
                    <MenuList>
                      <MenuItem icon={<RepeatClockIcon />} onClick={() => handleStatusChange(request.id, 'open')}>
                        Aberto
                      </MenuItem>
                      <MenuItem icon={<ChatIcon />} onClick={() => handleStatusChange(request.id, 'in_progress')}>
                        Em andamento
                      </MenuItem>
                      <MenuItem
                        icon={<TimeIcon />}
                        onClick={() => handleStatusChange(request.id, 'waiting_for_a_place')}
                      >
                        Aguardando Vaga
                      </MenuItem>
                      {['Carteira de Estudante ABRA', 'Prova em 2ª Chamada'].includes(request.tipoDeSolicitacao) && (
                        <MenuItem icon={<TimeIcon />} onClick={() => handleStatusChange(request.id, 'waiting_payment')}>
                          Aguardando pagamento
                        </MenuItem>
                      )}
                      <MenuItem
                        icon={<WarningTwoIcon />}
                        onClick={() => handleStatusChange(request.id, 'in_management_analysis')}
                      >
                        Em análise da gestão
                      </MenuItem>
                      <MenuItem icon={<CheckIcon />} onClick={() => handleStatusChange(request.id, 'solved')}>
                        Resolvido
                      </MenuItem>
                    </MenuList>
                  </Menu>
                </td>
                <td>
                  <Flex justifyContent="center" alignItems="center" direction={{ base: 'column', '2xl': 'row' }}>
                    {request.status == 'solved' && (
                      <Tooltip label={handleSolvedTooltip(request)} hasArrow>
                        <Button
                          colorScheme="abraGreen"
                          size={{ base: 'xs' }}
                          mb={{ base: '2', '2xl': '0' }}
                          mr={{ base: 0, '2xl': 1 }}>
                          <i className="lni lni-hourglass"></i>
                        </Button>
                      </Tooltip>)}
                    <Button
                      onClick={() => handleOpenModal(request.id)}
                      colorScheme="magenta"
                      variant="outline"
                      size={{ base: 'xs', '2xl': 'sm' }}
                      mb={{ base: '2', '2xl': '0' }}
                      ml={{ base: 0, '2xl': 1 }}
                      mr={{ base: 0, '2xl': 1 }}
                    >
                      <i className="lni lni-eye"></i>
                    </Button>
                    <Button
                      onClick={() => handleDeleteDialog(request)}
                      colorScheme="magenta"
                      size={{ base: 'xs', '2xl': 'sm' }}
                      ml={{ base: 0, '2xl': 1 }}
                    >
                      <i className="lni lni-trash-can"></i>
                    </Button>
                  </Flex>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Paginator
        page={page}
        totalPages={totalPages}
        changePage={(page: number) => {
          setPage(page)
          loadRequests(page)
        }}
      />

      <RequestViewModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        request={modalRequest}
        refreshRequest={refreshRequest}
      />

      <RequestDeleteDialog
        isOpen={deleteDialogIsOpen}
        onClose={deleteDialogIsClose}
        request={requestToDelete}
        leastDestructiveRef={cancelRef}
        onDelete={() => {
          deleteDialogIsClose()
          loadRequests(page)
        }}
      />
    </>
  )
}
